import { TFunction } from 'i18next'

import { Organization } from '@modules-deprecated/app/organization/types/organization'

import { ContactPerson } from '../../../contacts/types/contactPerson'
import { ZervantTemplateSettings } from '../../../invoicingSettings/types/templates/zervantTemplateSettings'
import { ZervantSettings } from '../../../invoicingSettings/types/zervantSettings'
import { getSettingsFieldsByDocumentType } from '../../../invoicingSettings/utils/getSettingsFieldsByDocumentType'
import { invoiceLocaleToLanguage } from '../../constants/invoiceLocaleToLanguage'
import { InvoiceBillingStage } from '../../enums/invoiceBillingStage'
import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { InvoiceEditorVersion } from '../../enums/invoiceEditorVersion'
import { DocumentDraftCreator } from '../../types/documentDraftCreator'
import { DepositInvoiceConfig } from '../../types/invoiceDeposit/depositInvoiceConfig'
import { InvoiceForm } from '../../types/invoiceForm/invoiceForm'
import { InvoiceLineColumn } from '../../types/invoiceLineColumn'
import { InvoicePayload } from '../../types/invoicePayload/invoicePayload'
import { Language } from '../../types/language'
import { isDocumentTypeCreditNote } from '../isDocumentTypeCreditNote'
import { isDocumentTypeQuote } from '../isDocumentTypeQuote'
import { isDocumentTypeRecurringInvoice } from '../isDocumentTypeRecurringInvoice'
import { getFieldsPayload } from './getFieldsPayload'
import { getInvoiceNumberPayload } from './getInvoiceNumberPayload'
import { getInvoicerPayload } from './getInvoicerPayload'
import { getLinesPayload } from './getLinesPayload'
import { getPaymentMeansPayload } from './getPaymentMeansPayload'
import { getReceiverPayload } from './getReceiverPayload'
import { getSummaryPayload } from './getSummaryPayload'

interface GetInvoicePayloadOptions {
  approvedInvoiceId?: string
  baseCurrency?: string
  billingStage?: InvoiceBillingStage
  creator?: DocumentDraftCreator
  customTemplateSettings?: ZervantTemplateSettings
  depositConfig?: DepositInvoiceConfig
  documentType: InvoiceDocumentType
  invoiceValues: InvoiceForm
  organization?: Organization
  receiverContactPerson?: ContactPerson
  t: TFunction
  visibleColumns: InvoiceLineColumn[]
  zervantSettings?: ZervantSettings
}

export const getInvoicePayload = ({
  approvedInvoiceId,
  baseCurrency,
  billingStage,
  creator,
  customTemplateSettings,
  depositConfig,
  documentType,
  invoiceValues,
  organization,
  receiverContactPerson,
  t,
  visibleColumns,
  zervantSettings,
}: GetInvoicePayloadOptions): InvoicePayload => {
  const settingsFields = getSettingsFieldsByDocumentType(zervantSettings, InvoiceDocumentType.Invoice)
  const invoiceLanguage = (invoiceLocaleToLanguage[invoiceValues?.language] ||
    invoiceValues.receiver?.invoicingLanguage ||
    settingsFields?.language?.default ||
    'en') as Language

  const isCreditNote = isDocumentTypeCreditNote(documentType)
  const isCreatingCreditNote = !!approvedInvoiceId && documentType === InvoiceDocumentType.CreditNote
  const isQuote = isDocumentTypeQuote(documentType)
  const model = isQuote ? 'models.estimate' : 'models.invoice.sales'

  const isRecurringInvoice = isDocumentTypeRecurringInvoice(documentType)

  // Recurring profiles create invoices, so we're operating on InvoiceDocumentType.Invoice when creating a template for a recurring invoice anyway.
  const document = isRecurringInvoice ? InvoiceDocumentType.Invoice : documentType

  return {
    ...(creator ? { creator } : {}),
    ...(customTemplateSettings ? { customSettings: { name: 'default', settings: customTemplateSettings } } : {}),
    ...(invoiceValues?.id ? { id: invoiceValues.id } : {}),
    document,
    eInvoiceSending: false,
    fields: getFieldsPayload({
      approvedEstimateId: invoiceValues.approvedEstimateId,
      approvedInvoiceId: isCreatingCreditNote ? approvedInvoiceId : undefined,
      billingStage,
      depositConfig,
      documentType,
      invoiceLanguage,
      invoiceValues,
      organization,
      t,
      visibleColumns,
      settingsFields,
    }),
    ...(isQuote ? {} : { invoiceNumber: getInvoiceNumberPayload(invoiceValues) }),
    invoicer: getInvoicerPayload(organization, zervantSettings?.values?.company),
    lines: getLinesPayload(invoiceValues, zervantSettings?.managedFields?.UNIT_OPTIONS ?? [], isCreditNote),
    model,
    paymentMeans: getPaymentMeansPayload(
      zervantSettings?.values?.paymentMethods,
      invoiceValues?.displayedPaymentMethods,
    ),
    receiver: getReceiverPayload({ formValues: invoiceValues, invoiceLanguage, receiverContactPerson }),
    summary: getSummaryPayload(invoiceValues, baseCurrency, isCreditNote),
    version: InvoiceEditorVersion.Ageras,
  }
}
